import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, ArrowRight } from 'lucide-react';
import { useInternalTransfers } from '@/hooks/finance/useInternalTransfers';
import { useBankAccounts } from '@/hooks/use-bank-accounts';
import { NewTransferDialog } from '@/components/finance/NewTransferDialog';
import { format } from 'date-fns';
import { formatCurrency } from '@/lib/currency';

const InternalTransfersPage: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { transfers, isLoading, createTransfer, isCreating, deleteTransfer } = useInternalTransfers();
  const { data: accounts = [] } = useBankAccounts();

  const getAccountName = (id: string) => {
    const acc = accounts.find(a => a.id === id);
    return acc ? acc.name : 'Unknown';
  };

  return (
    <Layout>
      <div className="w-full max-w-full py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Internal Transfers</h1>
            <p className="text-muted-foreground mt-1">
              Manage inter-account transfers. These are excluded from cash outflow reports.
            </p>
          </div>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> New Transfer
          </Button>
        </div>

        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>From → To</TableHead>
                <TableHead className="text-right">Amount Sent</TableHead>
                <TableHead className="text-right">Amount Received</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : transfers.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No transfers recorded yet</TableCell></TableRow>
              ) : (
                transfers.map(t => (
                  <TableRow key={t.id}>
                    <TableCell className="font-mono text-sm">{format(new Date(t.transfer_date), 'dd MMM yyyy')}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <span>{getAccountName(t.from_account_id)}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        <span>{getAccountName(t.to_account_id)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(t.from_amount, t.from_currency)}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(t.to_amount, t.to_currency)}</TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {t.from_currency !== t.to_currency && t.exchange_rate
                        ? t.exchange_rate.toFixed(4)
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {t.exchange_rate_source && (
                        <Badge variant={t.exchange_rate_source === 'system' ? 'default' : 'secondary'} className="text-xs">
                          {t.exchange_rate_source}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{t.reference_number || '—'}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => deleteTransfer(t.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <NewTransferDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSubmit={createTransfer}
          isSubmitting={isCreating}
        />
      </div>
    </Layout>
  );
};

export default InternalTransfersPage;
